import { VStack, Heading, useToast, Center, Spinner, Box } from "@chakra-ui/react";
import TreasureOpportunityForm from "./Form";
import { useNavigate, useParams } from "react-router-dom";
import { ROUTES } from "../../../consts/routes";
import useTreasureGameOpportunities from "./hooks/useTreasureGameOpportunities";
import { useEffect, useState } from "react";
import { AdminTreasureGameOpportunity } from "@olagg/db-types";
import { useAuthStore } from "@olagg/store";

interface TreasureOpportunityFormData {
    userId: string;
    notes?: string;
    expiresAt?: string;
    sourceType: string;
    sourceId: number;
    quantity?: number;
}

const EditTreasureOpportunity = () => {
    const toast = useToast();
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { getTreasureOpportunity, updateTreasureOpportunity } = useTreasureGameOpportunities();
    const [opportunity, setOpportunity] = useState<AdminTreasureGameOpportunity | null>(null);
    const [loading, setLoading] = useState(true);
    const { me } = useAuthStore();

    useEffect(() => {
        const fetchOpportunity = async () => {
            if (!id) {
                toast({
                    title: 'Error',
                    description: 'ID de oportunidad no encontrado',
                    status: 'error',
                    position: 'bottom-right',
                    duration: 3000,
                });
                navigate(ROUTES.MINI_GAMES.treasure_opportunities.all);
                return;
            }

            try {
                setLoading(true);
                const data = await getTreasureOpportunity(id);
                setOpportunity(data.treasureOpportunity);
            } catch (error: any) {
                console.error('Error fetching treasure opportunity:', error);
                toast({
                    title: 'Error',
                    description: 'Error al cargar la oportunidad extra',
                    status: 'error',
                    position: 'bottom-right',
                    duration: 3000,
                });
                navigate(ROUTES.MINI_GAMES.treasure_opportunities.all);
            } finally {
                setLoading(false);
            }
        };

        fetchOpportunity();
    }, [id, navigate, toast]);

    const onSubmit = async (dataForm: TreasureOpportunityFormData) => {
        if (!id) return;

        try {
            // Transformar los datos para enviar al backend (sin quantity para edición)
            const formattedData = {
                user_id: parseInt(dataForm.userId),
                notes: dataForm.notes || undefined,
                expires_at: dataForm.expiresAt || undefined,
                source_type: dataForm.sourceType,
                source_id: dataForm.sourceId,
            };

            await updateTreasureOpportunity(id, formattedData);
            
            toast({
                title: 'Oportunidad Extra Actualizada',
                colorScheme: 'olaggPink',
                status: 'success',
                description: 'Oportunidad extra actualizada con éxito',
                position: 'bottom-right',
                duration: 3000,
            });
            
            navigate(ROUTES.MINI_GAMES.treasure_opportunities.all);
        } catch (error: any) {
            console.error('Error updating treasure opportunity:', error);
            toast({
                title: 'Error',
                colorScheme: 'olaggYellow',
                status: 'error',
                description: error.message || 'Error al actualizar la oportunidad extra',
                position: 'bottom-right',
                duration: 3000,
            });
        }
    };

    if (loading) {
        return (
            <Center w="full" h="400px">
                <VStack spacing={4}>
                    <Spinner size="xl" color="white" />
                    <Heading size="md" color="white">
                        Cargando oportunidad extra...
                    </Heading>
                </VStack>
            </Center>
        );
    }

    if (!opportunity) {
        return (
            <Center w="full" h="400px">
                <Box color="white" textAlign="center">
                    <Heading size="md" mb={4}>
                        Oportunidad extra no encontrada
                    </Heading>
                </Box>
            </Center>
        );
    }

    // Preparar datos iniciales para el formulario
    const initialData = {
        userId: opportunity.userInfo.id.toString(),
        notes: opportunity.notes || '',
        expiresAt: opportunity.expiresAt ? new Date(opportunity.expiresAt).toISOString().slice(0, 16) : '',
        sourceType: 'User',
        sourceId: me?.id,
    };

    return (
        <Center w="full" pb={4}>
            <VStack color="white" w="full" spacing={6}>
                <Heading my={2} textAlign="center">
                    Editar Oportunidad Extra
                </Heading>
                <TreasureOpportunityForm 
                    onSubmit={onSubmit}
                    initialData={initialData}
                    isEditing={true}
                />
            </VStack>
        </Center>
    );
};

export default EditTreasureOpportunity;
